import { uploadSignatureFilestack, type IntentEnum } from '@goldcast/api/backend';

export interface FilestackSecurity {
  policy: string;
  signature: string;
}

export type FilestackIntentArgs =
  | { intent: Extract<IntentEnum, 'organization_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'org_filler_slide_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'public_image_org_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_series_asset'>; assetKind: string; seriesId: string }
  | { intent: Extract<IntentEnum, 'bucket_root_asset'>; assetKind: string };

function buildBody(args: FilestackIntentArgs): Record<string, string> {
  const body: Record<string, string> = { intent: args.intent, asset_kind: args.assetKind };
  if (args.intent === 'event_asset' || args.intent === 'public_image_event_asset') {
    body.event = args.event;
  } else if (args.intent === 'public_image_series_asset') {
    body.series_id = args.seriesId;
  }
  return body;
}

function buildCacheKey(args: FilestackIntentArgs): string {
  const event = 'event' in args ? args.event : 'none';
  const series = 'seriesId' in args ? args.seriesId : 'none';
  return `${args.intent}:${event}:${series}:${args.assetKind}`;
}

const EARLY_EXPIRY_BUFFER_MS = 60 * 1000;

const securityCache = new Map<string, { security: FilestackSecurity; expiresAtMs: number }>();

export async function getFilestackSecurity(args: FilestackIntentArgs): Promise<{ security: FilestackSecurity }> {
  const cacheKey = buildCacheKey(args);
  const cached = securityCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAtMs - EARLY_EXPIRY_BUFFER_MS) {
    return { security: cached.security };
  }

  const response = (await uploadSignatureFilestack({ body: buildBody(args) as never })) as {
    policy: string;
    signature: string;
    expiresAt: number;
  };
  const security = { policy: response.policy, signature: response.signature };
  securityCache.set(cacheKey, { security, expiresAtMs: response.expiresAt * 1000 });
  return { security };
}
